import { addDays } from "./tasks.js";
/**
 * A chore due date expressed as an offset from one of its project's dates
 * (#714).
 *
 * "Order the tile three days after demolition starts" is a real sentence about
 * a real chore, and before this it could only be written down as a calendar
 * date that stopped being true the moment the project moved. The anchor is the
 * sentence; `tasks.due_date` stays the answer.
 *
 * ── Why the resolved date is STORED, not computed at read time ───────────
 *
 * `tasks.due_date` is not a display field. It is the anchor every occurrence
 * generation path derives from, the column `tasks_household_due_idx` sorts, the
 * value the notification scheduler reads, and the date already copied onto
 * every `task_occurrences` row that has been materialized. Computing it on read
 * would mean teaching all of those to join a project — and it could not reach
 * the occurrence rows at all, which are persisted history, not a view.
 *
 * So this module is the ONE resolver, and it is called at WRITE time by the
 * three writes that can change the answer (see `resolveChoreDueDate`'s callers
 * in `apps/api/src/services/chores.ts` and `projects.ts`). That makes the
 * stored date a derived-persisted value, which is the defect class #1138 names
 * — so the refresh policy is declared rather than left to be discovered:
 *
 *   1. The chore is created with an anchor.
 *   2. The chore's anchor, offset, or project link is edited.
 *   3. The project's `startDate` / `targetEndDate` moves.
 *
 * and nowhere else. In particular NOT on read, NOT on completion, and NOT for
 * a chore whose anchor is `""` — an absolute date is a fact the household
 * stated, and nothing in this module may overwrite one.
 *
 * ── Why `""` is the third anchor value rather than a nullable column ─────
 *
 * Every chore that exists today is absolute, and the column has to be able to
 * say so about rows written before it existed. `NOT NULL DEFAULT ''` backfills
 * them with exactly today's behaviour, the same shape migration 153 used for
 * `repeat_from_completion`.
 */
/** The project date an offset can be counted from. */
export const choreDueAnchors = ["project_start", "project_target_end"];
/**
 * Ten years either way. Wide enough for "the year after handover", narrow
 * enough that a fat-fingered offset cannot push a chore past the date range
 * `dateOnlySchema` can even spell. Mirrored by the `tasks_due_offset_days_chk`
 * CHECK constraint, so the bound holds for a write that never sees zod.
 */
export const choreDueOffsetDaysMin = -3650;
export const choreDueOffsetDaysMax = 3650;
export function isChoreDueAnchor(value) {
    return typeof value === "string" && choreDueAnchors.includes(value);
}
export function isChoreDueAnchorValue(value) {
    return value === "" || isChoreDueAnchor(value);
}
/** The project date `anchor` names, or `""` when that date is not set. */
export function choreDueAnchorDate(anchor, project) {
    if (!anchor || !project)
        return "";
    return anchor === "project_start" ? project.startDate : project.targetEndDate;
}
/**
 * The calendar date an anchored chore is due, or `null` when the anchor cannot
 * be resolved right now.
 *
 * `null` is deliberately NOT an error and deliberately NOT `""`. It is the
 * "freeze" signal: the caller keeps whatever `due_date` the row already holds.
 * Two situations reach it, and both are ordinary:
 *
 *   - the chore is absolute (`anchor === ""`), so there is nothing to resolve;
 *   - the project exists but has not been given that date yet.
 *
 * Reverting the chore to "no due date" instead would be worse than useless: it
 * would drop a chore with live pending occurrences out of every dated surface
 * while those occurrence rows sat there still dated, which is the persisted /
 * derived split #1138 warns about pointed the other way.
 *
 * The arithmetic is `addDays`, which is `Date.UTC` throughout. Dates in this
 * app are local-day strings, never instants, so anchoring must not touch a
 * timezone: `"2026-03-01" + 1` is `"2026-03-02"` for a reader in Kiritimati and
 * for a reader in Niue, and a resolver built on local `Date` parsing would
 * disagree with one of them by a day.
 */
export function resolveChoreDueDate(anchor, offsetDays, project) {
    const anchorDate = choreDueAnchorDate(anchor, project);
    if (!anchorDate)
        return null;
    return addDays(anchorDate, clampChoreDueOffsetDays(offsetDays));
}
/** Bring an offset inside the stored range. Callers validate first; this is the
 * last line so a value read back from an older row can never throw a date out
 * of range. */
export function clampChoreDueOffsetDays(offsetDays) {
    if (!Number.isFinite(offsetDays))
        return 0;
    const whole = Math.trunc(offsetDays);
    return Math.min(choreDueOffsetDaysMax, Math.max(choreDueOffsetDaysMin, whole));
}
